import React from "react";
import { BlockMath, InlineMath } from "react-katex";

import EquationInput from "../../../../components/inputs/EquationInput";

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

function makeInterval(min = -7, max = 7) {
  const left = randInt(min, max - 2);
  const right = randInt(left + 1, max);
  return {
    left,
    right,
    leftClosed: Math.random() < 0.55,
    rightClosed: Math.random() < 0.55,
  };
}

function intervalLatex(interval) {
  const leftBracket = interval.leftClosed ? "[" : "(";
  const rightBracket = interval.rightClosed ? "]" : ")";
  return `${leftBracket}${interval.left}, ${interval.right}${rightBracket}`;
}

function transformDomain(domain, symmetry) {
  if (symmetry === "xAxis") return { ...domain };
  return {
    left: -domain.right,
    right: -domain.left,
    leftClosed: domain.rightClosed,
    rightClosed: domain.leftClosed,
  };
}

function transformRange(range, symmetry) {
  if (symmetry === "yAxis") return { ...range };
  return {
    left: -range.right,
    right: -range.left,
    leftClosed: range.rightClosed,
    rightClosed: range.leftClosed,
  };
}

function normalizeInterval(value) {
  return String(typeof value === "object" ? value?.raw ?? "" : value ?? "")
    .replace(/−/g, "-")
    .replace(/\\left|\\right/g, "")
    .replace(/\\langle|⟨/g, "[")
    .replace(/\\rangle|⟩/g, "]")
    .replace(/\\,/g, ",")
    .replace(/\s+/g, "")
    .replace(/^(D_g|Dg|Z_g|Zg|ZW_g|ZWg|W_g|Wg)=/i, "");
}

function intervalAccepted(interval) {
  const plain = intervalLatex(interval);
  const angle = plain.replace(/\[/g, "\\langle").replace(/\]/g, "\\rangle");
  return Array.from(new Set([normalizeInterval(plain), normalizeInterval(angle)]));
}

function intervalMatches(value, interval) {
  const got = normalizeInterval(value);
  return Boolean(got) && intervalAccepted(interval).includes(got);
}

function symmetryPick() {
  const r = Math.random();
  if (r < 1 / 3) return "xAxis";
  if (r < 2 / 3) return "yAxis";
  return "origin";
}

function symmetryPhrase(symmetry) {
  if (symmetry === "xAxis") {
    return (
      <>
        symetrię względem osi <InlineMath math={"OX"} />
      </>
    );
  }
  if (symmetry === "yAxis") {
    return (
      <>
        symetrię względem osi <InlineMath math={"OY"} />
      </>
    );
  }
  return <>symetrię względem początku układu współrzędnych</>;
}

function domainRule(symmetry) {
  return symmetry === "xAxis" ? "dziedzina zostaje bez zmian" : "dziedzina odbija się względem zera";
}

function rangeRule(symmetry) {
  return symmetry === "yAxis" ? "zbiór wartości zostaje bez zmian" : "zbiór wartości odbija się względem zera";
}

export function generateTask(forceSymmetry) {
  const symmetry = forceSymmetry || symmetryPick();
  const domain = makeInterval(-7, 7);
  const range = makeInterval(-6, 8);

  return {
    type: "SymmetryDomainRangeTask",
    data: {
      symmetry,
      domain,
      range,
      answerDomain: transformDomain(domain, symmetry),
      answerRange: transformRange(range, symmetry),
    },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div>
        Dana jest funkcja <InlineMath math={"f"} />, dla której:
        <BlockMath math={`D_f=${intervalLatex(data.domain)},\\quad ZW_f=${intervalLatex(data.range)}`} />
      </div>

      <div style={{ marginTop: 6 }}>
        Funkcja <InlineMath math={"g"} /> powstała przez {symmetryPhrase(data.symmetry)} wykresu funkcji{" "}
        <InlineMath math={"f"} />.
      </div>

      <div style={{ marginTop: 10 }}>
        Podaj dziedzinę i zbiór wartości funkcji <InlineMath math={"g"} />.
      </div>
    </div>
  );
}

export function renderInput(_task, userInput = {}, onChange, disabled) {
  const ui = userInput && typeof userInput === "object" ? userInput : {};
  const setField = (field) => (value) => onChange({ ...ui, [field]: value });

  return (
    <div className="flex flex-col gap-3" style={{ marginTop: 8 }}>
      <div className="flex items-center gap-2">
        <InlineMath math={"D_g="} />
        <div style={{ minWidth: 240 }}>
          <EquationInput
            value={ui.domain ?? ""}
            onChange={setField("domain")}
            disabled={disabled}
            showPreview={false}
            placeholder={"np. (-3, 5]"}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <InlineMath math={"ZW_g="} />
        <div style={{ minWidth: 240 }}>
          <EquationInput
            value={ui.range ?? ""}
            onChange={setField("range")}
            disabled={disabled}
            showPreview={false}
            placeholder={"np. [-2, 4)"}
          />
        </div>
      </div>
    </div>
  );
}

export function checkAnswer(task, userInput = {}) {
  return (
    intervalMatches(userInput.domain, task.data.answerDomain) &&
    intervalMatches(userInput.range, task.data.answerRange)
  );
}

export function renderAnswer({ data }) {
  return (
    <span>
      <InlineMath math={`D_g=${intervalLatex(data.answerDomain)}`} />
      {", "}
      <InlineMath math={`ZW_g=${intervalLatex(data.answerRange)}`} />
    </span>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Patrzymy osobno na argumenty i wartości funkcji.
      </div>
      <div>
        Dla tej symetrii {domainRule(data.symmetry)}, a {rangeRule(data.symmetry)}.
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Przekształcamy odpowiednie przedziały:
      </div>
      <BlockMath
        math={`D_g=${intervalLatex(data.answerDomain)},\\quad ZW_g=${intervalLatex(data.answerRange)}`}
      />
    </div>
  );
}

export function renderUserAnswer(arg1 = {}, arg2 = {}) {
  const userInput = arg1?.type === "SymmetryDomainRangeTask" ? arg2 : arg1;
  const domain = normalizeInterval(userInput.domain);
  const range = normalizeInterval(userInput.range);

  if (!domain && !range) return <i>(brak odpowiedzi)</i>;

  return (
    <span>
      <InlineMath math={`D_g=${domain || "?"}`} />
      {", "}
      <InlineMath math={`ZW_g=${range || "?"}`} />
    </span>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
